import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate } from "ra-core";
import { useState } from "react";

import { useIsMobile } from "@/hooks/use-mobile";
import { MobileContent } from "@/components/atomic-crm/layout/MobileContent";
import MobileHeader from "@/components/atomic-crm/layout/MobileHeader";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { agruparMisComisiones, euros, mesDe } from "../dominio/comisiones";
import { claveEtapa } from "../dominio/etapas";
import { formatoFecha } from "../negocios/formato";
import { useAbrirFicha } from "../negocios/useAbrirFicha";

/**
 * «Mis comisiones» (36, Robert 20/10/2026): lo que ve un setter de sus ventas (40: también el
 * consultor). Desde «Aceptada», la base firmada (sin IVA) y su comisión estimada; por cada cobro, el
 * importe, su comisión y si ya está pagada. Antes de «Aceptada» no hay importes. Lo calcula la base
 * (nevora_mis_comisiones), que solo devuelve lo suyo. 40: por meses (o todo).
 */
export const MisComisionesPage = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const abrirFicha = useAbrirFicha();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { data, isPending, error } = useQuery({
    queryKey: ["nevora", "mis-comisiones"],
    queryFn: () => dataProvider.misComisiones(),
  });
  // 40 (Robert, 23/10/2026): filtrar por meses; vacío = todo.
  const [mes, setMes] = useState(() => mesDe(new Date()));
  const { ventas, totalEstimada, totalCobrada, totalPagada } =
    agruparMisComisiones(data ?? [], mes || undefined);
  const variosPapeles = new Set(ventas.map((v) => v.papel)).size > 1;
  const t = (k: string, o?: Record<string, unknown>) =>
    translate(`nevora.mis_comisiones.${k}`, o);

  const cuerpo = (
    <div className="space-y-4 pb-8" data-testid="mis-comisiones">
      <p className="text-sm text-muted-foreground max-w-3xl">{t("ayuda")}</p>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-muted-foreground">{t("mes")}</span>
          <input
            type="month"
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
            data-testid="mis-comisiones-mes"
          />
        </label>
        <button
          type="button"
          className={
            mes
              ? "underline underline-offset-2 text-muted-foreground"
              : "font-medium"
          }
          onClick={() => setMes(mes ? "" : mesDe(new Date()))}
        >
          {mes ? t("ver_todo") : t("ver_mes")}
        </button>
      </div>
      {isPending ? (
        <p className="text-sm text-muted-foreground">{t("cargando")}</p>
      ) : error ? (
        <p className="text-sm text-destructive">
          {error instanceof Error ? error.message : String(error)}
        </p>
      ) : !ventas.length ? (
        <p className="text-sm text-muted-foreground">
          {mes && (data ?? []).length ? t("vacio_mes") : t("vacio")}
        </p>
      ) : (
        <>
          <dl className="grid grid-cols-3 gap-2">
            {[
              ["total_estimada", totalEstimada],
              ["total_cobrada", totalCobrada],
              ["total_pagada", totalPagada],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg border bg-card p-3">
                <dt className="text-xs text-muted-foreground">
                  {t(String(k))}
                </dt>
                <dd className="text-lg font-semibold tabular-nums">
                  {euros(Number(v))}
                </dd>
              </div>
            ))}
          </dl>
          <ul className="space-y-3">
            {ventas.map((v) => (
              <li
                key={`${v.proyecto_id}|${v.papel ?? ""}`}
                className="rounded-lg border bg-card p-4 space-y-2"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <button
                    type="button"
                    className="font-medium underline-offset-2 hover:underline text-left"
                    onClick={() => abrirFicha(v.negocio_id, v.proyecto_id)}
                  >
                    {v.negocio}
                  </button>
                  <span className="text-xs text-muted-foreground">
                    {variosPapeles && v.papel
                      ? `${translate(`nevora.comisiones.papeles.${v.papel}`)} · `
                      : ""}
                    {translate(claveEtapa(v.etapa))}
                    {v.aceptada_at
                      ? ` · ${t("aceptada_el", { fecha: formatoFecha(v.aceptada_at) })}`
                      : ""}
                  </span>
                </div>
                <dl className="grid grid-cols-2 gap-x-4 text-sm">
                  <dt className="text-muted-foreground">{t("base_firmada")}</dt>
                  <dd className="text-right tabular-nums">
                    {v.base_firmada != null ? euros(v.base_firmada) : "—"}
                  </dd>
                  <dt className="text-muted-foreground">{t("estimada")}</dt>
                  <dd className="text-right font-medium tabular-nums">
                    {v.comision_estimada != null
                      ? euros(v.comision_estimada)
                      : "—"}
                  </dd>
                </dl>
                {v.cobros.length ? (
                  <ul className="border-t pt-2 space-y-1 text-sm">
                    {v.cobros.map((c) => (
                      <li
                        key={String(c.cobro_id)}
                        className="flex flex-wrap items-center justify-between gap-x-3"
                      >
                        <span>
                          {t("cobro", {
                            fecha: c.cobrado_el
                              ? formatoFecha(c.cobrado_el)
                              : "",
                          })}
                          {" · "}
                          <span className="tabular-nums">
                            {euros(c.importe)}
                          </span>
                        </span>
                        <span className="tabular-nums">
                          {t("comision")}:{" "}
                          {c.comision != null ? euros(c.comision) : "—"}
                          {" · "}
                          <span
                            className={
                              c.comision_pagada_at
                                ? "text-emerald-700 dark:text-emerald-300"
                                : "text-muted-foreground"
                            }
                          >
                            {c.comision_pagada_at
                              ? translate("nevora.comisiones.pagada_el", {
                                  fecha: formatoFecha(c.comision_pagada_at),
                                })
                              : translate("nevora.comisiones.pendiente")}
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="border-t pt-2 text-sm text-muted-foreground">
                    {t("sin_cobros")}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <h1 className="titulo-seccion">{t("titulo")}</h1>
        </MobileHeader>
        <MobileContent>{cuerpo}</MobileContent>
      </>
    );
  }
  return (
    <div className="mt-2 space-y-4 max-w-3xl">
      <h1 className="titulo-pagina">{t("titulo")}</h1>
      {cuerpo}
    </div>
  );
};

MisComisionesPage.path = "/mis-comisiones";
